import { ApiError } from 'src/api/generated';
import type { AlertType } from 'src/contexts/alert-context';
import { sleep } from 'src/utils/clock';
import {
  HTTP_STATUS_BAD_REQUEST,
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_INTERNAL_SERVER_ERROR,
  HTTP_STATUS_NOT_FOUND,
  HTTP_STATUS_UNAUTHORIZED,
  HTTP_STATUS_UNPROCESSABLE_ENTITY,
  MAX_RETRY_DELAY_IN_MS,
  MAX_RETRY_NUMBER,
  MIN_RETRY_DELAY_IN_MS,
} from '../../constants/api/api.constants';
import { AuthTokenUnavailableError } from '../../errors/auth/auth-token-unavailable.error';
import type { ApiRequestCurrentGuard } from '../../types/api/api-retry-policy.type';
import type { TranslationKey } from '../../types/i18n/translation-keys.type';

/**
 * Calculates the retry delay using exponential backoff.
 *
 * @param retryAttempt - Current retry attempt number.
 * @returns Delay in milliseconds.
 */
export const exponentialBackoffDelay = (retryAttempt: number): number => {
  const exponentialDelay = Math.pow(2, retryAttempt) * MIN_RETRY_DELAY_IN_MS;
  return Math.min(exponentialDelay, MAX_RETRY_DELAY_IN_MS);
};

/**
 * Decides whether a failed query should be retried.
 *
 * @param failureCount - Current retry count.
 * @param error - Error raised by the request.
 * @param refreshToken - Token refresh callback used for auth failures.
 * @returns True when the query should retry.
 */
export const shouldRetry = (
  failureCount: number,
  error: Error,
  refreshToken: (options?: { forceRefresh?: boolean }) => Promise<void>
): boolean => {
  if (failureCount >= MAX_RETRY_NUMBER) {
    return false;
  }

  return shouldRetryError(error, refreshToken);
};

/**
 * Classifies whether a specific error is retryable.
 *
 * @param error - Error raised by the request.
 * @param refreshToken - Token refresh callback used for auth failures.
 * @returns True when the query should retry.
 */
export const shouldRetryError = (
  error: Error,
  refreshToken: (options?: { forceRefresh?: boolean }) => Promise<void>
): boolean => {
  if (error instanceof AuthTokenUnavailableError) {
    return error.retryable;
  }

  if (!(error instanceof ApiError)) {
    return true;
  }

  if (error.status === HTTP_STATUS_UNAUTHORIZED) {
    void refreshToken({ forceRefresh: false });
    return true;
  }

  if (error.status === HTTP_STATUS_FORBIDDEN) {
    void refreshToken({ forceRefresh: true });
    return true;
  }

  if (
    error.status === HTTP_STATUS_BAD_REQUEST ||
    error.status === HTTP_STATUS_NOT_FOUND ||
    error.status === HTTP_STATUS_UNPROCESSABLE_ENTITY
  ) {
    return false;
  }

  if (error.status === HTTP_STATUS_INTERNAL_SERVER_ERROR) {
    return true;
  }

  return true;
};

/**
 * Handles errors once query retries are exhausted.
 *
 * @param error - Error raised by the request.
 * @param logout - Logout callback used for expired sessions.
 * @param showAlert - Alert renderer used for user-facing feedback.
 * @param translate - Existing translation resolver.
 * @param isCurrent - Optional guard checked before alerts and delayed logout.
 * @returns Resolves after the terminal policy has been applied.
 */
export const onRetryLimitReached = async (
  error: Error,
  logout: () => Promise<void>,
  showAlert: (message: string, type: AlertType) => void,
  translate: (key: TranslationKey) => string,
  isCurrent: ApiRequestCurrentGuard = () => true
) => {
  if (!isCurrent() || !(error instanceof ApiError)) {
    return;
  }

  if (error.status === HTTP_STATUS_UNAUTHORIZED) {
    const message = translate('shared.api.retry.unauthorized');
    showAlert(message, 'warning');
    await sleep(5000);
    if (isCurrent()) void logout();
    return;
  }

  if (error.status === HTTP_STATUS_FORBIDDEN) {
    const message = translate('shared.api.retry.forbidden');
    showAlert(message, 'error');
  }
};
